/**
 * ScripsAIIcon — AI state indicator
 * Pure CSS keyframe animations · zero hooks · production-ready
 *
 * idle      — Breathe: whole mark gently inhales/exhales as one form
 * thinking  — Split open: brackets jaw apart, dot rises
 * streaming — Split held: brackets stay open, pulse in place
 * done      — Split close: brackets spring back, dot pops, plays once
 *
 * Tokens: blue-800 (#002F63) bg · blue-400 (#3391F9) ring · white paths
 * Source: design-tokens.ts — Figma 2026-04-03 (authoritative)
 */

import * as React from "react";

const TOKEN = {
  primaryDark: "#002F63",  // blue-800
  accentBlue:  "#3391F9",  // blue-400
  white:       "#FFFFFF",
};

const DOT =
  "M35.7964 41.3357C40.0077 41.3357 43.4327 37.9094 43.4327 33.6994C43.4327 29.4882 40.0077 26.0632 35.7964 26.0632C31.5864 26.0632 28.1602 29.4882 28.1602 33.6994C28.1602 37.9094 31.5864 41.3357 35.7964 41.3357Z";
const STEM =
  "M42.5041 48.249H29.0879V100.114H42.5041V48.249Z";
const BRACKET_BR =
  "M58.2374 63.0875V70.9238H58.2199V76.7175V89.815V90.9013C58.1937 91.9975 58.0899 93.0713 57.9124 94.1238C57.5649 96.1725 56.9437 98.1275 56.0812 99.9463C54.1137 104.1 50.9062 107.544 46.9387 109.818C45.5249 110.626 44.0187 111.291 42.4337 111.78C40.3424 112.425 38.1212 112.773 35.8174 112.773C33.5137 112.773 31.2924 112.425 29.2012 111.779V112.426V125.556C31.3462 125.956 33.5562 126.175 35.8174 126.175H35.8187C38.0799 126.175 40.2874 125.956 42.4337 125.556C48.3324 124.454 53.7212 121.909 58.2199 118.296C58.2237 118.293 58.2274 118.29 58.2324 118.286C58.4974 118.071 58.7599 117.854 59.0199 117.634C62.5199 114.655 65.4349 111.016 67.5812 106.909C68.2287 105.666 68.8074 104.383 69.3087 103.061C70.4112 100.159 71.1499 97.08 71.4624 93.875C71.5749 92.7175 71.6362 91.5463 71.6362 90.3588V90.3575V90.355V88.45V76.7188V70.9113V63.0875H58.2374Z";
const BRACKET_TL =
  "M13.3987 63.0874V55.2524H13.4162V49.4587V36.3612V35.2749C13.4425 34.1799 13.5463 33.1049 13.7238 32.0524C14.07 30.0037 14.6925 28.0487 15.555 26.2287C17.5225 22.0774 20.73 18.6312 24.6975 16.3587C26.1112 15.5499 27.6175 14.8849 29.2037 14.3962C31.2937 13.7512 33.5163 13.4037 35.8188 13.4037C38.1225 13.4037 40.3437 13.7512 42.435 14.3974V13.7512V0.621153C40.29 0.219903 38.0813 -9.91821e-05 35.8188 -9.91821e-05C33.5575 -9.91821e-05 31.3487 0.219904 29.2037 0.619904C23.3037 1.7224 17.9162 4.2674 13.4162 7.8799C13.4125 7.88365 13.4087 7.88615 13.4037 7.8899C13.1387 8.1049 12.8762 8.32115 12.6162 8.5424C9.1175 11.5212 6.20125 15.1599 4.055 19.2662C3.4075 20.5087 2.82875 21.7937 2.3275 23.1149C1.225 26.0174 0.48625 29.0962 0.17375 32.3012C0.0612499 33.4587 0 34.6299 0 35.8174V35.8187V35.8212V37.7262V49.4574V55.2649V63.0874L13.3987 63.0874Z";

const VW    = 71.636;
const VH    = 126.175;
const DOT_CX = 35.7964;
const DOT_CY = 33.6994;

export type AIState = "idle" | "thinking" | "streaming" | "done";

const STYLE_ID = "scrips-ai-icon-kf";
if (typeof document !== "undefined" && !document.getElementById(STYLE_ID)) {
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
  @keyframes scrips-idle-svg  { 0%,100%{transform:scale(1)} 50%{transform:scale(1.046)} }
  @keyframes scrips-idle-ring { 0%,100%{opacity:.10} 50%{opacity:.40} }
  @keyframes scrips-think-tl  { 0%,100%{transform:translate(0,0)} 50%{transform:translate(-9px,-5.5px)} }
  @keyframes scrips-think-br  { 0%,100%{transform:translate(0,0)} 50%{transform:translate(9px,5.5px)} }
  @keyframes scrips-think-dot { 0%,100%{transform:scale(1) translateY(0)} 50%{transform:scale(1.12) translateY(-4px)} }
  @keyframes scrips-think-ring{ 0%,100%{opacity:.18;transform:scale(1)} 50%{opacity:.70;transform:scale(1.05)} }
  @keyframes scrips-stream-tl { 0%,100%{transform:translate(-4px,-2.5px)} 50%{transform:translate(-7px,-4.5px)} }
  @keyframes scrips-stream-br { 0%,100%{transform:translate(4px,2.5px)} 50%{transform:translate(7px,4.5px)} }
  @keyframes scrips-stream-ring{ 0%,100%{opacity:.18;transform:scale(1)} 50%{opacity:.70;transform:scale(1.05)} }
  @keyframes scrips-done-tl   { 0%{transform:translate(-4px,-2.5px)} 40%{transform:translate(1px,.6px)} 70%{transform:translate(-.3px,-.2px)} 100%{transform:translate(0,0)} }
  @keyframes scrips-done-br   { 0%{transform:translate(4px,2.5px)} 40%{transform:translate(-1px,-.6px)} 70%{transform:translate(.3px,.2px)} 100%{transform:translate(0,0)} }
  @keyframes scrips-done-dot  { 0%{transform:scale(1)} 35%{transform:scale(1.45)} 60%{transform:scale(.92)} 100%{transform:scale(1)} }

  /* Reduced motion — the idle/thinking/streaming states are INFINITE
   * animations. Left unguarded they run forever for users who have asked
   * the OS for less motion (vestibular disorders, migraine triggers).
   *
   * CORRECTION (Codex review, 2026-08-12): an earlier version only set
   * animation:none and claimed in this comment that no information was
   * lost. That was WRONG and untested. Every bracket offset, the dot lift
   * and the ring opacity come ONLY from the keyframes — so killing the
   * animation collapsed idle, thinking and streaming into an IDENTICAL
   * closed mark with a fully opaque ring. Reduced-motion users lost the
   * state signal entirely, which is worse than no support.
   *
   * Each state now carries a STATIC pose equivalent to the animation's
   * characteristic frame: closed for idle, jaw fully open for thinking,
   * held open for streaming, closed with the ring dropped for done. The
   * movement goes; the meaning stays. */
  @media (prefers-reduced-motion: reduce) {
    [data-scrips-ai-icon],
    [data-scrips-ai-icon] * {
      animation: none !important;
      transition: none !important;
    }

    /* idle — closed mark, ring at the midpoint of its gentle pulse. */
    [data-scrips-ai-icon][data-state="idle"] [data-part="ring"] { opacity: .25; }

    /* thinking — brackets at full jaw, dot lifted: the busiest pose. */
    [data-scrips-ai-icon][data-state="thinking"] [data-part="tl"]  { transform: translate(-9px, -5.5px); }
    [data-scrips-ai-icon][data-state="thinking"] [data-part="br"]  { transform: translate(9px, 5.5px); }
    [data-scrips-ai-icon][data-state="thinking"] [data-part="dot"] { transform: scale(1.12) translateY(-4px); }
    [data-scrips-ai-icon][data-state="thinking"] [data-part="ring"]{ opacity: .70; }

    /* streaming — held open, but visibly narrower than thinking so the
     * two are distinguishable at rest, not merely both "open". */
    [data-scrips-ai-icon][data-state="streaming"] [data-part="tl"]  { transform: translate(-5.5px, -3.5px); }
    [data-scrips-ai-icon][data-state="streaming"] [data-part="br"]  { transform: translate(5.5px, 3.5px); }
    [data-scrips-ai-icon][data-state="streaming"] [data-part="ring"]{ opacity: .45; }

    /* done — closed and settled; the ring is already hidden inline. */
    [data-scrips-ai-icon][data-state="done"] [data-part="tl"],
    [data-scrips-ai-icon][data-state="done"] [data-part="br"] { transform: none; }
  }
  `;
  document.head.appendChild(s);
}

const ringBase: React.CSSProperties = {
  position: "absolute", inset: -1.5, borderRadius: "50%",
  border: `1.5px solid ${TOKEN.accentBlue}`, pointerEvents: "none",
};

function ring(state: AIState): React.CSSProperties {
  if (state === "idle")      return { ...ringBase, animation: "scrips-idle-ring 5s ease-in-out infinite" };
  if (state === "thinking")  return { ...ringBase, animation: "scrips-think-ring 1.8s ease-in-out infinite" };
  if (state === "streaming") return { ...ringBase, animation: "scrips-stream-ring 1.5s ease-in-out infinite" };
  return { ...ringBase, opacity: 0 };
}
function svgStyle(state: AIState): React.CSSProperties {
  if (state === "idle") return { animation: "scrips-idle-svg 5s ease-in-out infinite", transformOrigin: "50% 50%" };
  return {};
}
function tl(state: AIState): React.CSSProperties {
  if (state === "thinking")  return { animation: "scrips-think-tl 1.8s cubic-bezier(.4,0,.2,1) infinite", transformOrigin: "7px 63px" };
  if (state === "streaming") return { animation: "scrips-stream-tl 1.5s ease-in-out infinite", transformOrigin: "7px 63px" };
  if (state === "done")      return { animation: "scrips-done-tl .7s cubic-bezier(.34,1.56,.64,1) forwards", transformOrigin: "7px 63px" };
  return {};
}
function br(state: AIState): React.CSSProperties {
  if (state === "thinking")  return { animation: "scrips-think-br 1.8s cubic-bezier(.4,0,.2,1) .09s infinite", transformOrigin: "65px 63px" };
  if (state === "streaming") return { animation: "scrips-stream-br 1.5s ease-in-out .08s infinite", transformOrigin: "65px 63px" };
  if (state === "done")      return { animation: "scrips-done-br .7s cubic-bezier(.34,1.56,.64,1) forwards", transformOrigin: "65px 63px" };
  return {};
}
function dot(state: AIState): React.CSSProperties {
  const base: React.CSSProperties = { transformOrigin: `${DOT_CX}px ${DOT_CY}px` };
  if (state === "thinking") return { ...base, animation: "scrips-think-dot 1.8s ease-in-out infinite" };
  if (state === "done")     return { ...base, animation: "scrips-done-dot .65s cubic-bezier(.34,1.56,.64,1) forwards" };
  return base;
}

export interface ScripsAIIconProps {
  state?:   AIState;
  size?:    number;
  bgColor?: string;
  className?: string;
}

export const ScripsAIIcon: React.FC<ScripsAIIconProps> = ({
  state = "idle", size = 32, bgColor, className,
}) => {
  const pad = size * 0.16;
  return (
    <div
      className={className}
      data-scrips-ai-icon=""
      data-state={state}
      style={{
        width: size, height: size, borderRadius: "50%",
        background: bgColor ?? TOKEN.primaryDark,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: pad, boxSizing: "border-box",
        flexShrink: 0, position: "relative",
      }}
    >
      <div data-part="ring" style={ring(state)} />
      <svg
        viewBox={`0 0 ${VW} ${VH}`}
        style={{ width: "100%", height: "100%", overflow: "visible", ...svgStyle(state) }}
      >
        <path data-part="tl"   d={BRACKET_TL} fill={TOKEN.white} style={tl(state)} />
        <path data-part="br"   d={BRACKET_BR} fill={TOKEN.white} style={br(state)} />
        <path data-part="stem" d={STEM}       fill={TOKEN.white} />
        <path data-part="dot"  d={DOT}        fill={TOKEN.white} style={dot(state)} />
      </svg>
    </div>
  );
};

export default ScripsAIIcon;
